// Story 5.8 — TanStack Query mutation hook for POST
// /api/v1/account/kyc/initiate.
//
// FLOW
// ----
// The marketplace `ListForSaleDialog` / `ListSealedDossierForSaleDialog`
// surfaces a "Verify identity" CTA when the listing-creation response
// is 402 KYC_REQUIRED. The CTA fires this mutation. Two outcomes:
//   * `onboarding_url` present — a provider-hosted verification step
//     exists; the dialog opens it in a new tab per UX-DR L1238
//     state-preservation (the original tab stays mounted so the listing
//     draft survives the round-trip).
//   * `onboarding_url` null/absent — no hosted step exists (the beta's
//     mock provider satisfies verification server-side); the dialog
//     reflects the returned `account_status` instead of erroring.
//
// IDEMPOTENCY
// -----------
// A fresh per-call Idempotency-Key is required (covered by the
// FINANCIAL_MUTATION_PREFIXES entry "/api/v1/account/kyc/"). A
// network-retry replay returns the cached envelope and does NOT mint
// a second provider account.
//
// TYPED ERRORS THE CONSUMER CARES ABOUT
// -------------------------------------
//   * 400 VALIDATION_FAILED + field=email — user's email isn't verified
//   * 409 ALREADY_KYC_VERIFIED + tier — user is already at basic/enhanced
//   * 429 RATE_LIMITED — burnt 10/hour cap
//   * 502 BAAS_PROVIDER_ERROR — provider call failed transiently

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface InitiateKycOutcome {
  /** Null/absent when there is no hosted verification step (mocked KYC). */
  onboarding_url?: string | null;
  expires_at?: number | null;
  baas_account_id?: string;
  account_status?: string;
}

export interface InitiateKycError {
  code: string;
  message: string;
  field?: string;
  tier?: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postInitiateKyc(): Promise<InitiateKycOutcome> {
  const response = await fetch("/api/v1/account/kyc/initiate", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify({}),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: InitiateKycError = {
      code: "KYC_INITIATE_FAILED",
      message: `KYC initiation failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          tier: raw.error.tier,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  return (await response.json()) as InitiateKycOutcome;
}

export function useInitiateKyc(): UseMutationResult<
  InitiateKycOutcome,
  InitiateKycError,
  void
> {
  return useMutation<InitiateKycOutcome, InitiateKycError, void>({
    mutationFn: postInitiateKyc,
    // NO onSuccess cache invalidation — KYC initiation doesn't mutate
    // any client-side cached data (the tier flip happens later via the
    // provider's verification-changed notification).
  });
}
